import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { Plus, Building2, Grid3x3, Users, Trash2, Edit3, LogOut, X, AlertCircle, Euro, ArrowUp, Layers, Stethoscope, CheckCircle2, XCircle } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCIES = ['EUR', 'USD', 'TRY', 'GBP'];

const emptyForm = () => ({
  name: '', width: '', height: '', zone: '', grid_unit: '0.5',
  ceiling_height: '', max_stand_height: '',
  package_price_per_sqm: '', custom_price_per_sqm: '',
  default_currency: 'EUR'
});

const HallsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';

  // Token her API çağrısında taze okunur (stale closure sorununu önler)
  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token')}`
  });

  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Edit hall state
  const [editingHall, setEditingHall] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editSaving, setEditSaving] = useState(false);

  // Delete error
  const [deleteError, setDeleteError] = useState(null);

  // Diagnostic modal state (Phase 74 — production hall_stands debug)
  const [showDiag, setShowDiag] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagError, setDiagError] = useState(null);
  const [cleaningGhosts, setCleaningGhosts] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const runDiagnostic = async () => {
    setShowDiag(true);
    setDiagLoading(true);
    setDiagError(null);
    setDiagData(null);
    setCleanupResult(null);
    try {
      const res = await apiClient.get('/ultra-admin/diagnostics/halls');
      setDiagData(res.data);
    } catch (e) {
      setDiagError(e.response?.data?.detail || e.message || 'Bilinmeyen hata');
    }
    setDiagLoading(false);
  };

  const cleanupGhosts = async () => {
    if (!window.confirm(tr
      ? 'Ghost participant kayıtları temizlensin mi? Bu işlem stand_no ve hall referanslarını sıfırlar (geri alınamaz).'
      : 'Clean up ghost participant rows? This unsets stand_no / hall references (irreversible).'
    )) return;
    setCleaningGhosts(true);
    setCleanupResult(null);
    try {
      const res = await apiClient.post('/ultra-admin/diagnostics/halls/cleanup-ghosts');
      setCleanupResult(res.data);
      // Refresh diagnostic
      const fresh = await apiClient.get('/ultra-admin/diagnostics/halls');
      setDiagData(fresh.data);
    } catch (e) {
      setCleanupResult({ error: e.response?.data?.detail || e.message });
    }
    setCleaningGhosts(false);
  };

  useEffect(() => {
    fetchHalls();
    // Sayfa yeniden aktifleşince (agent uyandığında / tab'a dönüldüğünde) yeniden yükle
    const handleFocus = () => fetchHalls();
    const handleVisible = () => { if (document.visibilityState === 'visible') fetchHalls(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisible);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, []);

  const fetchHalls = async () => {
    setFetchError(false);
    try {
      const res = await apiClient.get(`/ultra-admin/halls`);
      setHalls(res.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status !== 401) {
        // 401 zaten global interceptor tarafından handle ediliyor
        setFetchError(true);
      }
    }
    setLoading(false);
  };

  const createHall = async () => {
    if (!form.name || !form.width || !form.height) {
      toast.error(tr ? 'Hall adı, genişlik ve yükseklik zorunludur' : 'Name, width and height are required');
      return;
    }
    setSaving(true);
    try {
      const unit = parseFloat(form.grid_unit) === 0.5 ? 0.5 : 1;
      await apiClient.post(`/ultra-admin/halls`, {
        name: form.name,
        width: Math.max(1, Math.round(parseFloat(form.width) / unit)),
        height: Math.max(1, Math.round(parseFloat(form.height) / unit)),
        grid_unit: unit,
        zone: form.zone || null,
        ceiling_height: form.ceiling_height ? parseFloat(form.ceiling_height) : null,
        max_stand_height: form.max_stand_height ? parseFloat(form.max_stand_height) : null,
        package_price_per_sqm: form.package_price_per_sqm ? parseFloat(form.package_price_per_sqm) : null,
        custom_price_per_sqm: form.custom_price_per_sqm ? parseFloat(form.custom_price_per_sqm) : null,
        default_price_per_sqm: form.package_price_per_sqm ? parseFloat(form.package_price_per_sqm) : null,
        default_currency: form.default_currency || 'EUR',
      });
      toast.success(tr ? 'Hall oluşturuldu' : 'Hall created');
      setForm(emptyForm());
      setShowCreate(false);
      fetchHalls();
    } catch (e) {
      // Surface backend error messages so the user understands why creation
      // failed (previously we only console.logged, which made the button
      // appear to "do nothing" — see user report 2026-05-04).
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || e?.message || (tr ? 'Hall oluşturulamadı' : 'Failed to create hall'));
      toast.error(msg);
      console.error('createHall failed:', e);
    }
    setSaving(false);
  };

  const openEditHall = (hall) => {
    setEditingHall(hall);
    const unit = hall.grid_unit === 0.5 ? 0.5 : 1;
    setEditForm({
      name: hall.name,
      width: String(hall.width * unit),
      height: String(hall.height * unit),
      grid_unit: String(unit),
      zone: hall.zone || '',
      ceiling_height: hall.ceiling_height != null ? String(hall.ceiling_height) : '',
      max_stand_height: hall.max_stand_height != null ? String(hall.max_stand_height) : '',
      package_price_per_sqm: hall.package_price_per_sqm != null ? String(hall.package_price_per_sqm) : '',
      custom_price_per_sqm: hall.custom_price_per_sqm != null ? String(hall.custom_price_per_sqm) : '',
      default_currency: hall.default_currency || 'EUR',
    });
  };

  const saveEditHall = async () => {
    if (!editForm.name || !editForm.width || !editForm.height) return;
    setEditSaving(true);
    try {
      const unit = parseFloat(editForm.grid_unit) === 0.5 ? 0.5 : 1;
      await apiClient.put(`/ultra-admin/halls/${editingHall.id}`, {
        name: editForm.name,
        width: Math.max(1, Math.round(parseFloat(editForm.width) / unit)),
        height: Math.max(1, Math.round(parseFloat(editForm.height) / unit)),
        grid_unit: unit,
        zone: editForm.zone || null,
        ceiling_height: editForm.ceiling_height ? parseFloat(editForm.ceiling_height) : null,
        max_stand_height: editForm.max_stand_height ? parseFloat(editForm.max_stand_height) : null,
        package_price_per_sqm: editForm.package_price_per_sqm ? parseFloat(editForm.package_price_per_sqm) : null,
        custom_price_per_sqm: editForm.custom_price_per_sqm ? parseFloat(editForm.custom_price_per_sqm) : null,
        default_price_per_sqm: editForm.package_price_per_sqm ? parseFloat(editForm.package_price_per_sqm) : null,
        default_currency: editForm.default_currency || 'EUR',
      });
      setEditingHall(null);
      toast.success(tr ? 'Hall güncellendi' : 'Hall updated');
      fetchHalls();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || e?.message || (tr ? 'Hall güncellenemedi' : 'Failed to update hall'));
      toast.error(msg);
      console.error('saveEditHall failed:', e);
    }
    setEditSaving(false);
  };

  const deleteHall = async (id, standCount) => {
    if (standCount > 0) {
      setDeleteError(tr
        ? `Bu hall'de ${standCount} stand var. Önce Hall Editörü'nden tüm standları silin, sonra hall'i silebilirsiniz.`
        : `This hall contains ${standCount} stand(s). Please remove all stands from the Hall Editor first, then delete the hall.`);
      return;
    }
    if (!window.confirm(tr ? 'Bu hall\'i silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this hall?')) return;
    try {
      await apiClient.delete(`/ultra-admin/halls/${id}`);
      fetchHalls();
    } catch (e) {
      const msg = e.response?.data?.detail || (tr ? 'Silme hatası oluştu.' : 'Delete failed.');
      setDeleteError(msg);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{tr ? 'Hall Yönetimi' : 'Hall Management'}</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {tr ? 'Hall oluşturun, stand alanları çizin ve katılımcı atayın'
                  : 'Create halls, draw stand areas and assign participants'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <button onClick={() => { localStorage.clear(); navigate('/giris'); }}
              className="text-gray-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 p-6">
          <PageIntro {...getPageIntro('halls', language)} />
          {/* Toolbar — Diagnostic + New Hall */}
          <div className="flex justify-end items-center gap-3 mb-6">
            <button
              onClick={runDiagnostic}
              data-testid="hall-diagnostic-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-all"
              title={tr ? 'Production veri durumunu raporla (read-only)' : 'Run read-only data diagnostic'}
            >
              <Stethoscope className="w-4 h-4" /> {tr ? 'Tanılama' : 'Diagnostic'}
            </button>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="create-hall-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
            >
              <Plus className="w-4 h-4" /> {tr ? 'Yeni Hall Oluştur' : 'Create New Hall'}
            </button>
          </div>

          {/* Create Modal */}
          {showCreate && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">{tr ? 'Yeni Hall Oluştur' : 'Create New Hall'}</h2>
                  <button onClick={() => { setShowCreate(false); setForm(emptyForm()); }}
                    className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Hall Adı *' : 'Hall Name *'}</label>
                    <input data-testid="hall-name-input" value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder={tr ? 'örn. B-Hall 1' : 'e.g. B-Hall 1'}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Zone</label>
                    <select value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400">
                      <option value="">{tr ? '— Seçiniz —' : '— Select —'}</option>
                      <option value="blue_zone">Blue Zone</option>
                      <option value="green_zone">Green Zone</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Grid Hassasiyeti' : 'Grid Precision'}</label>
                    <select data-testid="hall-grid-unit-select" value={form.grid_unit}
                      onChange={e => setForm(f => ({ ...f, grid_unit: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400">
                      <option value="1">{tr ? '1 m (tam sayı)' : '1 m (whole)'}</option>
                      <option value="0.5">{tr ? '0,5 m (buçuklu — ör. 12,5 × 10)' : '0.5 m (half — e.g. 12.5 × 10)'}</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Genişlik (m) *' : 'Width (m) *'}</label>
                      <input data-testid="hall-width-input" type="number" min={form.grid_unit === '0.5' ? 0.5 : 1} step={form.grid_unit === '0.5' ? 0.5 : 1} max="200" value={form.width}
                        onChange={e => setForm(f => ({ ...f, width: e.target.value }))} placeholder="70"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Derinlik (m) *' : 'Depth (m) *'}</label>
                      <input data-testid="hall-height-input" type="number" min={form.grid_unit === '0.5' ? 0.5 : 1} step={form.grid_unit === '0.5' ? 0.5 : 1} max="200" value={form.height}
                        onChange={e => setForm(f => ({ ...f, height: e.target.value }))} placeholder="50"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Tavan Yüksekliği (m)' : 'Ceiling Height (m)'}</label>
                      <input type="number" min="0" step="0.1" value={form.ceiling_height}
                        onChange={e => setForm(f => ({ ...f, ceiling_height: e.target.value }))} placeholder="6"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Maks. Stand Yük. (m)' : 'Max Stand Height (m)'}</label>
                      <input type="number" min="0" step="0.1" value={form.max_stand_height}
                        onChange={e => setForm(f => ({ ...f, max_stand_height: e.target.value }))} placeholder="5"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Para Birimi' : 'Currency'}</label>
                    <select value={form.default_currency} onChange={e => setForm(f => ({ ...f, default_currency: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400">
                      {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Paket Stand (€/m²)' : 'Package Stand (€/m²)'}</label>
                      <input type="number" min="0" step="0.01" value={form.package_price_per_sqm}
                        onChange={e => setForm(f => ({ ...f, package_price_per_sqm: e.target.value }))} placeholder="800"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Özel Stand (€/m²)' : 'Custom Stand (€/m²)'}</label>
                      <input type="number" min="0" step="0.01" value={form.custom_price_per_sqm}
                        onChange={e => setForm(f => ({ ...f, custom_price_per_sqm: e.target.value }))} placeholder="1000"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400" />
                    </div>
                  </div>
                  {form.width && form.height && (
                    <div className="bg-purple-50 rounded-xl px-4 py-2 text-xs text-purple-700">
                      <strong>{+(parseFloat(form.width) * parseFloat(form.height)).toFixed(2)} m²</strong>
                      {tr ? ' toplam alan • ' : ' total area • '}
                      {form.width}m × {form.height}m
                    </div>
                  )}
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => { setShowCreate(false); setForm(emptyForm()); }}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                    {tr ? 'İptal' : 'Cancel'}
                  </button>
                  <button onClick={createHall} disabled={saving || !form.name || !form.width || !form.height}
                    data-testid="hall-save-btn"
                    className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-all"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    {saving ? (tr ? 'Oluşturuluyor...' : 'Creating...') : (tr ? 'Oluştur' : 'Create')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete error toast */}
          {deleteError && (
            <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-xl px-4 py-3 shadow-lg max-w-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700">{tr ? 'Silinemedi' : 'Could not delete'}</p>
                <p className="text-xs text-red-600 mt-0.5">{deleteError}</p>
              </div>
              <button onClick={() => setDeleteError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Hall List */}
          {loading ? (
            <div className="text-center py-16 text-gray-400">{tr ? 'Yükleniyor...' : 'Loading...'}</div>
          ) : fetchError ? (
            // Halllar yüklenemedi — boş state ile karıştırma!
            <div className="text-center py-20">
              <AlertCircle className="w-16 h-16 text-amber-300 mx-auto mb-4" />
              <p className="text-gray-600 font-semibold">{tr ? 'Hall verileri yüklenemedi' : 'Could not load hall data'}</p>
              <p className="text-gray-400 text-sm mt-1 mb-5">
                {tr ? 'Sunucu yeniden başlamış olabilir. Hall planlarınız kaybolmadı.'
                    : 'The server may have just restarted. Your hall plans are safe.'}
              </p>
              <button
                onClick={() => { setLoading(true); fetchHalls(); }}
                className="px-6 py-2 rounded-xl text-white text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
              >
                {tr ? 'Yeniden Dene' : 'Retry'}
              </button>
            </div>
          ) : halls.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">{tr ? 'Henüz hall yok' : 'No halls yet'}</p>
              <p className="text-gray-400 text-sm mt-1">{tr ? "İlk hall'inizi oluşturun" : 'Create your first hall'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {halls.map(hall => {
                const isBlue  = hall.zone === 'blue_zone';
                const isGreen = hall.zone === 'green_zone';
                const barColor = isBlue ? '#2563eb' : isGreen ? '#059669' : '#7c3aed';
                const hu = hall.grid_unit === 0.5 ? 0.5 : 1;
                const totalArea = +(hall.width * hall.height * hu * hu).toFixed(2);
                return (
                <div key={hall.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden group">
                  {/* Zone renk şeridi */}
                  <div className="h-1.5 w-full" style={{ background: barColor }} />
                  <div className="p-4">
                    {/* Başlık + Zone badge + Aksiyon */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-800 text-sm">{hall.name}</h3>
                          {isBlue  && <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-medium">Blue Zone</span>}
                          {isGreen && <span className="text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-medium">Green Zone</span>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{+(hall.width * hu).toFixed(1)}m × {+(hall.height * hu).toFixed(1)}m</p>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEditHall(hall)}
                          className="p-1.5 rounded-lg text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteHall(hall.id, hall.stand_count)}
                          className="p-1.5 rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Alan + Standlar */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className={`rounded-xl p-2 text-center ${isBlue ? 'bg-blue-50' : isGreen ? 'bg-emerald-50' : 'bg-purple-50'}`}>
                        <p className={`text-base font-bold ${isBlue ? 'text-blue-800' : isGreen ? 'text-emerald-800' : 'text-purple-800'}`}>{totalArea}</p>
                        <p className={`text-[10px] ${isBlue ? 'text-blue-500' : isGreen ? 'text-emerald-500' : 'text-purple-500'}`}>m²</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-2 text-center">
                        <p className="text-base font-bold text-gray-700">{hall.stand_count ?? 0}</p>
                        <p className="text-[10px] text-gray-400">{tr ? 'Stand' : 'Stands'}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-2 text-center">
                        <p className="text-base font-bold text-green-700">{hall.assigned_count ?? 0}</p>
                        <p className="text-[10px] text-green-500">{tr ? 'Atanmış' : 'Assigned'}</p>
                      </div>
                    </div>

                    {/* Teknik bilgiler */}
                    <div className="space-y-1.5 mb-3">
                      {hall.ceiling_height && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400 flex items-center gap-1"><Layers className="h-3 w-3" /> {tr ? 'Tavan Yüksekliği' : 'Ceiling Height'}</span>
                          <span className="font-semibold text-gray-700">{hall.ceiling_height} m</span>
                        </div>
                      )}
                      {hall.max_stand_height && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400 flex items-center gap-1"><ArrowUp className="h-3 w-3" /> {tr ? 'Maks. Stand Yüksekliği' : 'Max Stand Height'}</span>
                          <span className="font-semibold text-gray-700">{hall.max_stand_height} m</span>
                        </div>
                      )}
                    </div>

                    {/* Fiyatlar */}
                    <div className="rounded-xl overflow-hidden border border-gray-100">
                      {hall.package_price_per_sqm && (
                        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                          <span className="text-[11px] text-gray-500">{tr ? 'Paket Stand' : 'Package Stand'}</span>
                          <span className="text-xs font-bold text-gray-800">{hall.package_price_per_sqm} {hall.default_currency || 'EUR'}/m²</span>
                        </div>
                      )}
                      {hall.custom_price_per_sqm && (
                        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50">
                          <span className="text-[11px] text-gray-500">{tr ? 'Özel Stand' : 'Custom Stand'}</span>
                          <span className="text-xs font-bold text-gray-800">{hall.custom_price_per_sqm} {hall.default_currency || 'EUR'}/m²</span>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => navigate(`/ultra-admin/halls/${hall.id}`)}
                      className="w-full mt-3 py-2 rounded-xl text-xs font-semibold text-white transition-all"
                      style={{ background: barColor }}>
                      {tr ? 'Hall Editörü →' : 'Hall Editor →'}
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Hall Modal */}
      {editingHall && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">{tr ? 'Hall Düzenle' : 'Edit Hall'}</h2>
              <button onClick={() => setEditingHall(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Hall Adı *' : 'Hall Name *'}</label>
                <input
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Grid Hassasiyeti' : 'Grid Precision'}</label>
                <select data-testid="edit-hall-grid-unit-select" value={editForm.grid_unit}
                  onChange={e => setEditForm(f => ({ ...f, grid_unit: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400">
                  <option value="1">{tr ? '1 m (tam sayı)' : '1 m (whole)'}</option>
                  <option value="0.5">{tr ? '0,5 m (buçuklu — ör. 12,5 × 10)' : '0.5 m (half — e.g. 12.5 × 10)'}</option>
                </select>
                {editingHall && String(editingHall.grid_unit === 0.5 ? '0.5' : '1') !== editForm.grid_unit && (
                  <p className="text-[11px] text-amber-600 mt-1.5 leading-snug" data-testid="edit-hall-grid-warning">
                    {tr
                      ? '⚠ Grid hassasiyeti değişecek. Mevcut standların fiziksel boyutları korunur (otomatik dönüştürülür).'
                      : '⚠ Grid precision will change. Existing stands keep their physical size (auto-converted).'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Genişlik (m) *' : 'Width (m) *'}</label>
                  <input type="number" min={editForm.grid_unit === '0.5' ? 0.5 : 1} step={editForm.grid_unit === '0.5' ? 0.5 : 1} max="200"
                    value={editForm.width}
                    onChange={e => setEditForm(f => ({ ...f, width: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Derinlik (m) *' : 'Depth (m) *'}</label>
                  <input type="number" min={editForm.grid_unit === '0.5' ? 0.5 : 1} step={editForm.grid_unit === '0.5' ? 0.5 : 1} max="200"
                    value={editForm.height}
                    onChange={e => setEditForm(f => ({ ...f, height: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Varsayılan m² Birim Fiyatı' : 'Default Price per m²'}</label>
                <div className="flex gap-2">
                  <input type="number" min="0" step="0.01"
                    value={editForm.default_price_per_sqm}
                    onChange={e => setEditForm(f => ({ ...f, default_price_per_sqm: e.target.value }))}
                    placeholder="0.00"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-400"
                  />
                  <select value={editForm.default_currency}
                    onChange={e => setEditForm(f => ({ ...f, default_currency: e.target.value }))}
                    className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-purple-400">
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditingHall(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                {tr ? 'İptal' : 'Cancel'}
              </button>
              <button onClick={saveEditHall} disabled={editSaving || !editForm.name || !editForm.width || !editForm.height}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-all"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                {editSaving ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Kaydet' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Diagnostic Modal (Phase 74) ─────────────────────────────────────── */}
      {showDiag && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-amber-600" />
                <h2 className="text-lg font-bold text-gray-800">
                  {tr ? 'Hall Veri Tanılama' : 'Hall Data Diagnostic'}
                </h2>
              </div>
              <button onClick={() => setShowDiag(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {diagLoading && (
                <div className="text-center text-gray-500 py-8">
                  {tr ? 'Tanılama çalışıyor...' : 'Running diagnostic...'}
                </div>
              )}
              {diagError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                  <strong>{tr ? 'Hata:' : 'Error:'}</strong> {String(diagError)}
                </div>
              )}
              {diagData && (
                <>
                  {/* Verdict card */}
                  <div className={`rounded-xl border p-4 ${
                    diagData.verdict?.likely_root_cause === 'ok'
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-3">
                      {diagData.verdict?.likely_root_cause === 'ok'
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        : <AlertCircle className="w-5 h-5 text-amber-600" />}
                      <h3 className="font-bold text-gray-800">
                        {tr ? 'Sonuç' : 'Verdict'}: {diagData.verdict?.likely_root_cause}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {Object.entries(diagData.verdict || {}).filter(([k]) => k !== 'likely_root_cause').map(([k, v]) => (
                        <div key={k} className="flex items-center gap-2">
                          {v ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                          <span className="text-gray-700">{k}: <strong>{String(v)}</strong></span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Totals */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-2">{tr ? 'Toplam Sayılar' : 'Totals'}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      {Object.entries(diagData.totals || {}).map(([k, v]) => (
                        <div key={k} className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                          <div className="text-gray-500">{k}</div>
                          <div className="font-bold text-gray-800 text-base">{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Per Hall */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-2">{tr ? 'Hall Başına' : 'Per Hall'}</h3>
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Zone</th>
                            <th className="px-3 py-2 text-right">Stands</th>
                            <th className="px-3 py-2 text-right">Assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(diagData.per_hall || []).map(h => (
                            <tr key={h.id} className="border-t border-gray-100">
                              <td className="px-3 py-2 font-medium text-gray-800">{h.name}</td>
                              <td className="px-3 py-2 text-gray-600">{h.zone || '—'}</td>
                              <td className={`px-3 py-2 text-right font-semibold ${h.stand_count === 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                                {h.stand_count}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">{h.assigned_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Orphans / Ghosts */}
                  {(diagData.orphan_stands?.length > 0 || diagData.ghost_participants?.length > 0) && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs space-y-2">
                      {diagData.orphan_stands?.length > 0 && (
                        <div>
                          <strong className="text-red-700">Orphan stands:</strong> {diagData.orphan_stands.length}
                          <pre className="mt-1 text-[10px] text-red-800 overflow-x-auto">{JSON.stringify(diagData.orphan_stands, null, 2)}</pre>
                        </div>
                      )}
                      {diagData.ghost_participants?.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <strong className="text-red-700">Ghost participants: {diagData.ghost_participants.length}</strong>
                            <button
                              onClick={cleanupGhosts}
                              disabled={cleaningGhosts}
                              data-testid="cleanup-ghosts-btn"
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-all"
                            >
                              <Trash2 className="w-3 h-3" />
                              {cleaningGhosts
                                ? (tr ? 'Temizleniyor...' : 'Cleaning...')
                                : (tr ? 'Ghost Temizle' : 'Clean Ghosts')}
                            </button>
                          </div>
                          <pre className="mt-1 text-[10px] text-red-800 overflow-x-auto">{JSON.stringify(diagData.ghost_participants, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cleanup result banner */}
                  {cleanupResult && (
                    <div className={`rounded-xl border p-3 text-xs ${
                      cleanupResult.error
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    }`}>
                      {cleanupResult.error
                        ? <><strong>{tr ? 'Hata:' : 'Error:'}</strong> {String(cleanupResult.error)}</>
                        : <><strong>✓</strong> {cleanupResult.message}</>}
                    </div>
                  )}

                  {/* Copy raw JSON button */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(diagData, null, 2));
                      alert(tr ? 'Rapor JSON olarak kopyalandı.' : 'Report copied as JSON.');
                    }}
                    className="w-full py-2.5 rounded-xl border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {tr ? 'Tüm Raporu Kopyala (JSON)' : 'Copy Full Report (JSON)'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HallsPage;
